// React.
import { useContext, useEffect, useMemo, useState } from "react";

// Third-party packages.
import { Link, NavLink, useNavigate } from "react-router-dom";
import * as QueryString from "query-string";

// Contexts.
import {InstanceContext} from "../../../context/InstanceContext";

// Components.
import ConditionalRender from "../../../helpers/ConditionalRender";
import Breadcrumb from "../../breadcrumb";
import CopyUrl from "../../../component/buttons/CopyUrl";
import PageContent from "../../page_content"
import ParameterComparisonOptionsCard from "./parameter-comparison-options-card";
import ParameterComparisonTable from "./parameter-comparison-table";

//Types
import { ParameterComparisonProps, ParameterComparisonOptions } from "./types";
import InstanceTarget from "../../../types/instance/InstanceTarget";

// Helpers
import { formatPeriod, getDefaultPeriodDates, getTimeZone } from "../../../helpers/utils";

const ParameterComparison = (_props: ParameterComparisonProps) => {

    const searchParams = new URLSearchParams(window.location.search);
    const {instances} = useContext(InstanceContext);
    const tz = getTimeZone();
    const defaults = getDefaultPeriodDates(tz);

    const getInstanceName = (id: any) => {
        const instance = instances.find(instance => instance.id === parseInt(id));
        return instance ? instance.name : '';
    };

    const firstFrom = searchParams.get('firstInstanceFrom');
    const firstTo = searchParams.get('firstInstanceTo');
    const secondFrom = searchParams.get('secondInstanceFrom');
    const secondTo = searchParams.get('secondInstanceTo');

    const parameterComparisonInitialOptions: ParameterComparisonOptions = {
        firstInstanceOptions: {
            instanceId: parseInt(searchParams.get('firstInstanceId') || '-1', 10) || -1,
            instanceType: undefined,
            periodStart: firstFrom ? new Date(firstFrom) : defaults.firstPeriodStart,
            periodEnd: firstTo ? new Date(firstTo) : defaults.firstPeriodEnd,
            instanceName: getInstanceName(searchParams.get('firstInstanceId'))
        },
        secondInstanceOptions: {
            instanceId: parseInt(searchParams.get('secondInstanceId') || '-1', 10) || -1,
            instanceType: undefined,
            periodStart: secondFrom ? new Date(secondFrom) : defaults.secondPeriodStart,
            periodEnd: secondTo ? new Date(secondTo) : defaults.secondPeriodEnd,
            instanceName: getInstanceName(searchParams.get('secondInstanceId'))
        }
    }

    const navigate = useNavigate();
    const timezone = tz

    const [options, setOptions] = useState<ParameterComparisonOptions>(parameterComparisonInitialOptions)
    const [generateId, setGenerateId] = useState<number>(0)
    const [showComparisonTable, setShowComparisonTable] = useState<boolean>(false)
    const [formattedBreadCrumb, setFormattedBreadCrumb] = useState("");

    const onOptionChange = (optionName: string, value: any) => {
        setOptions({...options, [optionName]: value})
    }

    const updateUrlParams = (type: string, value: string) => {
        const query = window.location.search;
        let queryString = QueryString.parse(query);
        queryString[type] = value
        navigate(`?${QueryString.stringify(queryString)}`)
    }

    const resetFilters = () => {
        setOptions(parameterComparisonInitialOptions);
    };

    useEffect(() => {
        if (generateId) {
            updateUrlParams('firstInstanceId', options.firstInstanceOptions.instanceId.toString());
            updateUrlParams('firstInstanceFrom', options.firstInstanceOptions.periodStart.toISOString());
            updateUrlParams('firstInstanceTo', options.firstInstanceOptions.periodEnd.toISOString());
            updateUrlParams('secondInstanceId', options.secondInstanceOptions.instanceId.toString());
            updateUrlParams('secondInstanceFrom', options.secondInstanceOptions.periodStart.toISOString());
            updateUrlParams('secondInstanceTo', options.secondInstanceOptions.periodEnd.toISOString());
            updateUrlParams('generate', 'false')
            if (options.firstInstanceOptions.snapshotId) {
                updateUrlParams('firstInstanceSnapshotId', options.firstInstanceOptions.snapshotId.toString())
            }
            if (options.secondInstanceOptions.snapshotId) {
                updateUrlParams('secondInstanceSnapshotId', options.secondInstanceOptions.snapshotId.toString())
            }
        }
    }, [generateId])

    const onGenerate = () => {
        setShowComparisonTable(true);
        setGenerateId(new Date().getTime())
    }

    useEffect(() => {
        if (searchParams.get('generate') === 'true') {
            setShowComparisonTable(true);
            setGenerateId(new Date().getTime());
        }
    }, [options.firstInstanceOptions.instanceType, options.secondInstanceOptions.instanceType])


    // useEffect(() => {
    //     if (searchParams.get('generate') === 'true'){
    //         setGenerateId(new Date().getTime());
    //         setShowComparisonTable(true);
    //     }
    // }, [options.firstInstanceOptions.instanceType, options.secondInstanceOptions.instanceType])

    const availableInstances = useMemo(() => {
        return instances
                .filter((instance: InstanceTarget) => instance.detectchanges)
                .sort((a, b) => a.name.localeCompare(b.name));
    }, [instances]);

    useEffect(() => {
        if (!options?.firstInstanceOptions || !options?.secondInstanceOptions) {
            setFormattedBreadCrumb("");
            return;
        }

        const firstFormatted = formatPeriod(options.firstInstanceOptions, timezone);
        const secondFormatted = formatPeriod(options.secondInstanceOptions, timezone);

        setFormattedBreadCrumb(`${firstFormatted} / ${secondFormatted} (${timezone})`);
    }, [generateId]);

    return <>
        <PageContent title='Parameter Comparison - DBmarlin' description={''}>
            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Parameter Comparison">
                    <Link to="#">Reports</Link>
                    <NavLink to="/reports/parameter-comparison" className={"current-breadcrumb-link"}>Parameter Comparison</NavLink>
                    <span>{formattedBreadCrumb}</span>

                </Breadcrumb>
                <div className="col col-lg-5 col-md-6 col-sm-6 order-last icon text-md-end actions ms-auto">
                    <CopyUrl/>
                </div>
            </div>
            <div className="row row-cols-1">
                <div className="col">
                    <ParameterComparisonOptionsCard loading={false} options={options}
                                                    resetFilters={resetFilters}
                                                    instances={availableInstances}
                                                    onOptionsChange={onOptionChange} onGenerate={onGenerate}/>
                    <ConditionalRender if={showComparisonTable}>
                        <ParameterComparisonTable
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            options={options} generateId={generateId}/>
                    </ConditionalRender>
                </div>
            </div>
        </PageContent>
    </>
};

export default ParameterComparison;
